import { createLearnerApi, updateLearnerApi } from "@/api/services/main/learner";
import { IconSave } from "@/assets/icons";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { LEARNER_WIDTH_FULL } from "@/features/learner/constants/width";
import { LearnerBasicInfoForm } from "@/features/learner/components/learner-form/LearnerBasicInfoForm";
import { LearnerExamInfoForm } from "@/features/learner/components/learner-form/LearnerExamInfoForm";
import { LearnerOtherInfoForm } from "@/features/learner/components/learner-form/LearnerOtherInfoForm";
import { LearnerResidenceInfoForm } from "@/features/learner/components/learner-form/LearnerResidenceInfoForm";
import { LearnerStatusForm } from "@/features/learner/components/learner-form/LearnerStatusForm";
import { LearnerCurriculumForm } from "@/features/learner/components/learner-form/LearnerCurriculumForm";
import { LearnerTitle } from "@/features/learner/components/LearnerTitle";
import { useGetLearnerSchema } from "@/features/learner/hook/useGetLearnerSchema";
import { TLearnerForm } from "@/features/learner/types/learnerForm";
import { convertLearnerFormDataReq, convertLearnerFormDataUpdateReq } from "@/features/learner/utils/convertLearnerFormData";
import { AppRoutes } from "@/routes/config";
import { FCC } from "@/types/common";
import { getDynamicRoute } from "@/utils/url";
import { toast } from "@/utils/toast";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { LearnerAccountInfo } from "@/features/learner/components/LearnerAccountInfo";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMe } from "@/api/services/main/auth";
import { getIndustryPartTypes, useLearnerIndustryOptions, TLearnerIndustryWithCurriculum } from "@/features/learner/hook/useLearnerIndustryOptions";
import { showError } from "@/utils/error";
import { getNextOrCurrentMonday } from "@/features/learner/utils/calculates";
import { USER_ACTIVE_STATUS } from "@/types/enum";
import { TIndustryExam } from "@/api/services/main/company/dtos/get-company-list.dto";

type TLearnerFormProps = {
  defaultValues?: Partial<TLearnerForm>;
  /** Persisted 手続ステータス — read-only context for the procedure-management toggle confirm modal. Not edited via this form. */
  procedureStatusKey?: string | null;
};

export const LearnerForm: FCC<TLearnerFormProps> = ({ defaultValues, procedureStatusKey }) => {
  const navigate = useNavigate();
  const { learnerId } = useParams();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { learnerSchema, isRsoUser } = useGetLearnerSchema();
  const { data: meResult } = useMe();

  const formDefaults = useMemo(
    () => ({
      ...defaultValues,
      // Create: start OFF (no industry yet → app locked OFF) to avoid mount normalization flipping it
      // and marking the pristine form dirty. Edit: use saved value. Industry-change effect re-evaluates.
      isUsingLearningApp: defaultValues ? (defaultValues.isUsingLearningApp ?? true) : false,
      isProcedureManaged: defaultValues?.isProcedureManaged ?? true,
      learnerPlan: {
        ...defaultValues?.learnerPlan,
        learningStartDate: defaultValues?.learnerPlan?.learningStartDate ?? getNextOrCurrentMonday(),
      },
    }),
    [defaultValues],
  );

  const methods = useForm<TLearnerForm>({
    defaultValues: formDefaults,
    resolver: yupResolver(learnerSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, setValue } = methods;

  const rsoCompanyIdWatch = useWatch({ control, name: "rsoCompanyId" });
  const companyId = isRsoUser ? rsoCompanyIdWatch : meResult?.data?.companyId;

  // Fallback industry — the learner's saved industry, used when it has been removed from the
  // company so the edit screen can still display name + saved exam label.
  const fallbackIndustry = useMemo<(Partial<TLearnerIndustryWithCurriculum> & { id: number; name: string }) | null>(() => {
    const ind = defaultValues?.industry;
    if (!ind?.id) return null;
    const savedExam = defaultValues?.learnerPlan?.exam;
    const fallbackExams: TIndustryExam[] = savedExam
      ? [
          {
            id: String(savedExam.id),
            periodName: savedExam.periodName,
            status: "",
            examMethod: "",
            examMonth: null,
          },
        ]
      : [];
    return {
      id: ind.id,
      name: ind.name,
      subjects: ind.subjects ?? [],
      exams: fallbackExams,
    };
  }, [defaultValues]);

  const { industryOptions, getIndustryById, isLoading: isLoadingIndustryOptions } = useLearnerIndustryOptions(fallbackIndustry);

  // Reset industryId when RSO user changes company
  const prevCompanyId = useRef(companyId);
  useEffect(() => {
    if (prevCompanyId.current != null && prevCompanyId.current !== companyId) {
      setValue("industryId", "", { shouldDirty: true });
    }
    prevCompanyId.current = companyId;
  }, [companyId, setValue]);

  const industryIdWatchField = useWatch({ control, name: "industryId" });
  const isUsingLearningAppWatch = useWatch({ control, name: "isUsingLearningApp" });

  // Whether the learner already has an issued (active) learning-app account.
  const hasIssuedAccount = useMemo(
    () =>
      !!(
        defaultValues?.userId &&
        defaultValues?.user &&
        !defaultValues.user.isArchive &&
        defaultValues.user.activeStatus === USER_ACTIVE_STATUS.ACTIVE
      ),
    [defaultValues],
  );

  const selectedIndustry = getIndustryById(industryIdWatchField);
  const selectedPartTypes = useWatch({ control, name: "learnerPlan.partTypes" });

  const industryPartTypes = useMemo(() => getIndustryPartTypes(selectedIndustry), [selectedIndustry]);

  const subjectCount = useMemo(() => {
    const subjects = selectedIndustry?.subjects ?? [];
    // Subject list available → count (filtered by selected partTypes when applicable).
    if (subjects.length) {
      // No selectable parts (e.g. 分割なし) → count all subjects
      if (!industryPartTypes.length) return subjects.length;
      // Has parts → count only subjects matching selected partTypes
      const parts = selectedPartTypes ?? [];
      return subjects.filter((s) => parts.includes(s.partType)).length;
    }
    // Fallback when /industries doesn't provide the subject list → use the flat subjectCount.
    return selectedIndustry?.subjectCount ?? null;
  }, [selectedIndustry, selectedPartTypes, industryPartTypes]);

  // Navigation helpers
  const getBackRoute = () => {
    return learnerId ? getDynamicRoute(AppRoutes.learnerDetail, { learnerId }) : AppRoutes.learnerList;
  };

  const handleBackClick = () => {
    navigate(getBackRoute());
  };

  const onSubmit = async (data: TLearnerForm) => {
    setIsSubmitting(true);
    try {
      if (learnerId) {
        const payload = convertLearnerFormDataUpdateReq(data);
        await updateLearnerApi(learnerId, payload);
        toast.success(API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_DETAIL, learnerId] });
        navigate(getDynamicRoute(AppRoutes.learnerDetail, { learnerId }));
      } else {
        const payload = convertLearnerFormDataReq(data);
        const res = await createLearnerApi(payload);
        toast.success(API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_LIST] });
        const newLearnerId = res?.data?.id;
        if (newLearnerId) {
          navigate(getDynamicRoute(AppRoutes.learnerDetail, { learnerId: String(newLearnerId) }));
        } else {
          navigate(AppRoutes.learnerList);
        }
      }
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <LearnerTitle
        fullName={learnerId && defaultValues?.fullName ? defaultValues.fullName : undefined}
        planStatus={learnerId && defaultValues?.learnerPlan?.planStatus ? defaultValues.learnerPlan.planStatus : undefined}
        createdAt={defaultValues?.createdAt}
        updatedAt={defaultValues?.updatedAt}
      />
      <Form methods={methods} onSubmit={onSubmit} schema={learnerSchema} initialValues={formDefaults as Partial<TLearnerForm>}>
        <Box width={`${LEARNER_WIDTH_FULL}px`}>
          <MenuNavigate
            menuPrefix={
              learnerId && defaultValues?.fullName ? (
                <Typography variant="body14Bold" sx={{ mb: 1 }}>
                  {defaultValues.fullName}
                </Typography>
              ) : undefined
            }
            contents={[
              { title: "ステータス", content: <LearnerStatusForm data={defaultValues} /> },
              { title: "基本情報", content: <LearnerBasicInfoForm /> },
              { title: "在留資格情報", content: <LearnerResidenceInfoForm /> },
              {
                title: "分野・日本語能力・試験記録",
                content: <LearnerExamInfoForm isLoadingIndustryList={isLoadingIndustryOptions} industryOptions={industryOptions} />,
              },
              { title: "その他の情報", content: <LearnerOtherInfoForm /> },
              {
                title: "受講カリキュラム",
                content: (
                  <LearnerCurriculumForm
                    subjectCount={subjectCount}
                    hasSubjects={(selectedIndustry?.subjectCount ?? selectedIndustry?.subjects?.length ?? 0) > 0}
                    subjects={selectedIndustry?.subjects ?? []}
                    mockExamQuestionCount={selectedIndustry?.mockExamQuestionCount ?? null}
                    industryPartTypes={industryPartTypes}
                    industryExams={selectedIndustry?.exams ?? []}
                    hasIndustry={!!industryIdWatchField}
                    isLoadingIndustry={isLoadingIndustryOptions}
                    hasIssuedAccount={hasIssuedAccount}
                    procedureStatusKey={procedureStatusKey ?? null}
                  />
                ),
              },
              // Hide the account-management section when the learning app is turned OFF.
              ...(isUsingLearningAppWatch !== false
                ? [
                    {
                      title: "学習アプリのアカウント管理",
                      content: (
                        <LearnerAccountInfo
                          data={defaultValues}
                          disabledAccountBtn={true}
                          industryHasNoSubjects={!!industryIdWatchField && !!selectedIndustry && (selectedIndustry.subjectCount ?? selectedIndustry.subjects?.length ?? 0) === 0}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            contentWidth="900px"
            contentAffix={
              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" gap={2}>
                    <Button variant="ghost" size="sm" onClick={handleBackClick}>
                      戻る
                    </Button>
                  </Stack>
                  <Button type="submit" variant="primary" startIcon={<IconSave />} size="sm" disabled={isSubmitting}>
                    保存
                  </Button>
                </Stack>
              </FooterContainer>
            }
          />
        </Box>
      </Form>
    </>
  );
};
